<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useBranding } from 'shared/composables/useBranding';
import JuyangAiAPI from 'dashboard/api/juyangAi';

import Button from 'dashboard/components-next/button/Button.vue';
import AssistantForm from './components/AssistantForm.vue';
import InboxesPanel from './components/InboxesPanel.vue';
import KnowledgePanel from './components/KnowledgePanel.vue';
import PlaygroundPanel from './components/PlaygroundPanel.vue';
import ScenariosPanel from './components/ScenariosPanel.vue';
import SettingPanel from './components/SettingPanel.vue';
import ToolsPanel from './components/ToolsPanel.vue';

const { t } = useI18n();
const { isAdmin } = useAdmin();
const { aiName } = useBranding();

const setting = ref({
  enabled: false,
  endpoint: 'https://api.openai.com',
  model: 'gpt-4.1-mini',
  embedding_endpoint: '',
  embedding_model: 'text-embedding-3-small',
  allowed_tool_hosts: [],
  agent_mode_available: false,
  configured: false,
});
const assistants = ref([]);
const selectedId = ref(null);
const activeTab = ref('settings');
const isLoading = ref(true);
const isSavingSetting = ref(false);
const isSavingAssistant = ref(false);
const isCreating = ref(false);

const selectedAssistant = computed(() =>
  assistants.value.find(item => item.id === selectedId.value)
);

const tabs = computed(() => {
  const values = [
    {
      value: 'settings',
      label: t('JUYANG_AI.ASSISTANT.SETTINGS'),
      icon: 'i-lucide-settings-2',
    },
    {
      value: 'knowledge',
      label: t('JUYANG_AI.ASSISTANT.KNOWLEDGE'),
      icon: 'i-lucide-book-open',
    },
  ];

  if (setting.value.agent_mode_available && isAdmin.value) {
    values.push(
      {
        value: 'tools',
        label: t('JUYANG_AI.ASSISTANT.TOOLS'),
        icon: 'i-lucide-wrench',
      },
      {
        value: 'scenarios',
        label: t('JUYANG_AI.ASSISTANT.SCENARIOS'),
        icon: 'i-lucide-workflow',
      }
    );
  }

  if (isAdmin.value) {
    values.push({
      value: 'playground',
      label: t('JUYANG_AI.ASSISTANT.PLAYGROUND'),
      icon: 'i-lucide-message-square-text',
    });
  }

  values.push({
    value: 'inboxes',
    label: t('JUYANG_AI.ASSISTANT.INBOXES'),
    icon: 'i-lucide-inbox',
  });

  return values;
});

const errorMessage = error =>
  error?.response?.data?.errors?.join(', ') ||
  error?.response?.data?.error ||
  t('JUYANG_AI.ERROR');

const loadAssistants = async preferredId => {
  const { data } = await JuyangAiAPI.getAssistants();
  assistants.value = data;

  const nextId = preferredId ?? selectedId.value;
  selectedId.value = data.some(item => item.id === nextId)
    ? nextId
    : data[0]?.id || null;
};

const loadPage = async () => {
  isLoading.value = true;
  try {
    const [{ data }] = await Promise.all([
      JuyangAiAPI.getSetting(),
      loadAssistants(),
    ]);
    setting.value = data;
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isLoading.value = false;
  }
};

const saveSetting = async payload => {
  isSavingSetting.value = true;
  try {
    const { data } = await JuyangAiAPI.updateSetting(payload);
    setting.value = data;
    useAlert(t('JUYANG_AI.SETTINGS.SUCCESS'));
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingSetting.value = false;
  }
};

const saveAssistant = async payload => {
  isSavingAssistant.value = true;
  try {
    if (isCreating.value) {
      const { data } = await JuyangAiAPI.createAssistant(payload);
      await loadAssistants(data.id);
      isCreating.value = false;
      useAlert(t('JUYANG_AI.ASSISTANT.CREATE_SUCCESS'));
    } else {
      const { data } = await JuyangAiAPI.updateAssistant(
        selectedId.value,
        payload
      );
      const index = assistants.value.findIndex(item => item.id === data.id);
      assistants.value.splice(index, 1, data);
      useAlert(t('JUYANG_AI.ASSISTANT.UPDATE_SUCCESS'));
    }
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSavingAssistant.value = false;
  }
};

const deleteAssistant = async () => {
  if (!window.confirm(t('JUYANG_AI.ASSISTANT.DELETE_CONFIRM'))) return;

  try {
    await JuyangAiAPI.deleteAssistant(selectedId.value);
    await loadAssistants();
    useAlert(t('JUYANG_AI.ASSISTANT.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(errorMessage(error));
  }
};

const selectAssistant = id => {
  selectedId.value = id;
  isCreating.value = false;
  activeTab.value = 'settings';
};

const beginCreate = () => {
  isCreating.value = true;
  activeTab.value = 'settings';
};

onMounted(loadPage);
</script>

<template>
  <div class="flex h-full min-h-0 flex-col overflow-y-auto bg-n-background">
    <header class="border-b border-n-weak">
      <div
        class="flex w-full items-center justify-between gap-4 px-4 py-5 md:px-6 xl:px-8"
      >
        <div>
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ t('JUYANG_AI.TITLE', { aiName }) }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('JUYANG_AI.DESCRIPTION') }}
          </p>
        </div>
        <Button
          v-if="isAdmin"
          icon="i-lucide-plus"
          :label="t('JUYANG_AI.CREATE_ASSISTANT')"
          :disabled="isCreating"
          @click="beginCreate"
        />
      </div>
    </header>

    <SettingPanel
      :setting="setting"
      :disabled="!isAdmin"
      :is-saving="isSavingSetting"
      @save="saveSetting"
    />

    <div
      v-if="isLoading"
      class="grid min-h-80 flex-1 place-content-center text-sm text-n-slate-11"
    >
      {{ t('JUYANG_AI.LOADING', { aiName }) }}
    </div>

    <div
      v-else
      class="grid min-h-[38rem] w-full flex-1 grid-cols-1 md:grid-cols-[17rem_minmax(0,1fr)]"
    >
      <aside
        class="border-b border-n-weak p-4 md:border-b-0 md:border-r md:p-5"
      >
        <ul class="m-0 flex list-none gap-2 overflow-x-auto p-0 md:flex-col">
          <li v-for="assistant in assistants" :key="assistant.id">
            <button
              type="button"
              class="flex min-w-48 items-center justify-between gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors md:min-w-0 md:w-full"
              :class="
                selectedId === assistant.id && !isCreating
                  ? 'bg-n-alpha-2 text-n-slate-12'
                  : 'text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="selectAssistant(assistant.id)"
            >
              <span class="min-w-0 truncate">{{ assistant.name }}</span>
              <span
                class="size-2 shrink-0 rounded-full"
                :class="assistant.enabled ? 'bg-n-teal-9' : 'bg-n-slate-7'"
              />
            </button>
          </li>
        </ul>
      </aside>

      <!-- 主工作区铺满可用宽度，避免宽屏内容集中在左侧。 -->
      <main class="min-w-0 p-4 md:p-6 xl:p-8">
        <div v-if="isCreating" class="flex w-full flex-col gap-5">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ t('JUYANG_AI.ASSISTANT.CREATE') }}
          </h2>
          <AssistantForm
            :agent-mode-available="setting.agent_mode_available"
            :is-saving="isSavingAssistant"
            @save="saveAssistant"
            @cancel="isCreating = false"
          />
        </div>

        <div v-else-if="selectedAssistant" class="flex w-full flex-col gap-6">
          <div class="flex flex-col gap-4 border-b border-n-weak pb-4">
            <div class="flex items-center justify-between gap-4">
              <div class="min-w-0">
                <h2 class="truncate text-lg font-medium text-n-slate-12">
                  {{ selectedAssistant.name }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{ selectedAssistant.description }}
                </p>
              </div>
              <Button
                v-if="isAdmin"
                icon="i-lucide-trash-2"
                color="ruby"
                variant="ghost"
                :title="t('JUYANG_AI.DELETE')"
                @click="deleteAssistant"
              />
            </div>

            <div class="flex gap-1 overflow-x-auto" role="tablist">
              <button
                v-for="tab in tabs"
                :key="tab.value"
                type="button"
                class="flex h-9 shrink-0 items-center gap-2 rounded-lg px-3 text-sm"
                :class="
                  activeTab === tab.value
                    ? 'bg-n-alpha-2 font-medium text-n-slate-12'
                    : 'text-n-slate-11 hover:bg-n-alpha-2'
                "
                role="tab"
                :aria-selected="activeTab === tab.value"
                @click="activeTab = tab.value"
              >
                <span class="size-4" :class="[tab.icon]" />
                {{ tab.label }}
              </button>
            </div>
          </div>

          <AssistantForm
            v-if="activeTab === 'settings' && isAdmin"
            :assistant="selectedAssistant"
            :agent-mode-available="selectedAssistant.agent_mode_available"
            :is-saving="isSavingAssistant"
            @save="saveAssistant"
          />
          <div
            v-else-if="activeTab === 'settings'"
            class="whitespace-pre-wrap text-sm text-n-slate-11"
          >
            {{ selectedAssistant.instructions }}
          </div>
          <KnowledgePanel
            v-else-if="activeTab === 'knowledge'"
            :assistant-id="selectedAssistant.id"
            :editable="isAdmin"
          />
          <ToolsPanel
            v-else-if="activeTab === 'tools'"
            :assistant-id="selectedAssistant.id"
            :editable="isAdmin"
            @changed="loadAssistants(selectedAssistant.id)"
          />
          <ScenariosPanel
            v-else-if="activeTab === 'scenarios'"
            :assistant-id="selectedAssistant.id"
            :editable="isAdmin"
            @changed="loadAssistants(selectedAssistant.id)"
          />
          <PlaygroundPanel
            v-else-if="activeTab === 'playground'"
            :key="selectedAssistant.id"
            :assistant-id="selectedAssistant.id"
          />
          <InboxesPanel
            v-else-if="activeTab === 'inboxes'"
            :assistant-id="selectedAssistant.id"
            :editable="isAdmin"
            @changed="loadAssistants(selectedAssistant.id)"
          />
        </div>

        <div v-else class="grid min-h-80 place-content-center text-center">
          <span class="i-lucide-bot mx-auto size-8 text-n-slate-9" />
          <h2 class="mt-3 text-base font-medium text-n-slate-12">
            {{ t('JUYANG_AI.EMPTY_TITLE') }}
          </h2>
          <p class="mt-1 max-w-md text-sm text-n-slate-11">
            {{ t('JUYANG_AI.EMPTY_DESCRIPTION') }}
          </p>
          <Button
            v-if="isAdmin"
            class="mx-auto mt-4"
            icon="i-lucide-plus"
            :label="t('JUYANG_AI.CREATE_ASSISTANT')"
            @click="beginCreate"
          />
        </div>
      </main>
    </div>
  </div>
</template>
